import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Switch } from "@v2/components/ui/switch";
import { supabaseV2 } from "../../lib/supabase";
import { currentIntlLocale } from "../../lib/i18n";
import {
  addDays,
  romeDate,
  seasonErrorMessage,
  suggestSeasonName,
  todayRome,
  type Season,
} from "../../lib/seasons";
import { useWorkspaceData } from "../../lib/workspace-data";
import { SectionLoading } from "../../components/state";
import type { Match, Session } from "../../types";

const BOUNDARY_DAYS = 14;
type Side = "old" | "new";

/**
 * Chiusura guidata in 4 passaggi: data di taglio, zona di confine,
 * esclusioni, nomi e conferma. Tutto viene applicato in una sola RPC.
 */
export function CloseSeasonWizard({
  open,
  onOpenChange,
  teamId,
  season,
  existingNames,
  onClosed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  season: Season;
  existingNames: string[];
  onClosed: (newSeasonId: string) => void;
}) {
  const { t } = useTranslation("seasons");
  const { events: sessions, responses, isLoading } = useWorkspaceData(teamId, { seasonId: season.id });
  const matchesQuery = useQuery({
    queryKey: ["matches", teamId, "close-wizard", season.id],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("matches")
        .select("*, sessions!inner(season_id)")
        .eq("team_id", teamId)
        .eq("sessions.season_id", season.id)
        .order("starts_at");
      if (error) throw error;
      return (data || []).map(({ sessions: _s, ...row }: any) => row) as Match[];
    },
  });
  const matches = useMemo(() => matchesQuery.data || [], [matchesQuery.data]);

  const today = todayRome();
  const maxCut = addDays(today, -1);
  const canClose = season.starts_on <= maxCut;

  const [step, setStep] = useState(0);
  const [cutDate, setCutDate] = useState("");
  const [overrides, setOverrides] = useState<Map<string, Side>>(new Map());
  const [matchCounts, setMatchCounts] = useState<Map<string, boolean>>(new Map());
  // Conferma esplicita quando le esclusioni svuoterebbero le statistiche.
  const [exclusionAck, setExclusionAck] = useState(false);
  const [closingName, setClosingName] = useState(season.name);
  const [newName, setNewName] = useState("");
  const [newNameDirty, setNewNameDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closedSeason, setClosedSeason] = useState<Season | null>(null);

  const suggestedCut = useMemo(() => suggestCutDate(sessions, season.starts_on, today), [sessions, season.starts_on, today]);

  // Reset solo all'apertura: dopo la chiusura `season` diventa la nuova
  // stagione in corso e il dialog deve restare sul passaggio finale.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setOverrides(new Map());
    setMatchCounts(new Map());
    setExclusionAck(false);
    setClosingName(season.name);
    setNewNameDirty(false);
    setError(null);
    setClosedSeason(null);
    setCutDate("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && !cutDate && !isLoading && canClose) setCutDate(suggestedCut);
  }, [open, cutDate, isLoading, suggestedCut, canClose]);

  useEffect(() => {
    if (newNameDirty || !cutDate) return;
    // Nome suggerito dalle date; se e' gia' usato (es. taglio a meta' anno) si passa all'anno dopo.
    const taken = new Set([season.name, ...existingNames].map((n) => n.trim().toLowerCase()));
    let start = addDays(cutDate, 1);
    let candidate = suggestSeasonName(start);
    for (let guard = 0; guard < 10 && taken.has(candidate.toLowerCase()); guard += 1) {
      start = `${Number(start.slice(0, 4)) + 1}${start.slice(4)}`;
      candidate = suggestSeasonName(start);
    }
    setNewName(candidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cutDate, newNameDirty]);

  const zoneFrom = cutDate ? addDays(cutDate, -BOUNDARY_DAYS) : "";
  const zoneTo = cutDate ? addDays(cutDate, BOUNDARY_DAYS) : "";
  const dateOf = (s: Session) => romeDate(s.starts_at);
  const inZone = (s: Session) => !!cutDate && dateOf(s) >= zoneFrom && dateOf(s) <= zoneTo;
  const defaultSide = (s: Session): Side => (dateOf(s) > cutDate ? "new" : "old");
  const sideOf = (s: Session): Side => (inZone(s) ? overrides.get(s.id) ?? defaultSide(s) : defaultSide(s));

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    [sessions],
  );
  const zoneSessions = sortedSessions.filter(inZone);
  const oldSessions = sortedSessions.filter((s) => sideOf(s) === "old");
  const newSessions = sortedSessions.filter((s) => sideOf(s) === "new");
  const oldSessionIds = new Set(oldSessions.map((s) => s.id));
  const oldMatches = matches.filter((m) => oldSessionIds.has(m.session_id));
  const countsOf = (m: Match) => matchCounts.get(m.id) ?? m.counts_for_stats !== false;
  const excludedCount = oldMatches.filter((m) => !countsOf(m)).length;
  const needsExclusionAck = excludedCount > 0 && excludedCount * 2 >= oldMatches.length;

  const presentBySession = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of responses) {
      if (r.status === "present" || r.status === "late") map.set(r.session_id, (map.get(r.session_id) || 0) + 1);
    }
    return map;
  }, [responses]);
  const matchesBySession = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of matches) map.set(m.session_id, (map.get(m.session_id) || 0) + 1);
    return map;
  }, [matches]);

  const lowerNames = existingNames.map((n) => n.trim().toLowerCase());
  const nameError = (() => {
    const a = closingName.trim();
    const b = newName.trim();
    if (!a || !b) return t("errors.nameRequired");
    if (a.toLowerCase() === b.toLowerCase() || lowerNames.includes(a.toLowerCase()) || lowerNames.includes(b.toLowerCase())) {
      return t("errors.nameDuplicate");
    }
    return null;
  })();
  const cutError = !cutDate || cutDate < season.starts_on || cutDate > maxCut ? t("wizard.cut.invalid", { max: formatDay(maxCut) }) : null;

  const close = useMutation({
    mutationFn: async () => {
      const toNew = zoneSessions.filter((s) => sideOf(s) === "new" && defaultSide(s) === "old").map((s) => s.id);
      const toOld = zoneSessions.filter((s) => sideOf(s) === "old" && defaultSide(s) === "new").map((s) => s.id);
      const exclude = oldMatches.filter((m) => m.counts_for_stats !== false && !countsOf(m)).map((m) => m.id);
      const include = oldMatches.filter((m) => m.counts_for_stats === false && countsOf(m)).map((m) => m.id);
      const { data, error: rpcError } = await supabaseV2.rpc("close_season", {
        _season_id: season.id,
        _cut_date: cutDate,
        _closing_name: closingName.trim(),
        _new_name: newName.trim(),
        _to_new_session_ids: toNew,
        _to_old_session_ids: toOld,
        _exclude_match_ids: exclude,
        _include_match_ids: include,
      });
      if (rpcError) throw rpcError;
      return data as Season;
    },
    onSuccess: (created) => {
      setClosedSeason(created);
      setStep(4);
      onClosed(created.id);
    },
    onError: (e: any) => setError(seasonErrorMessage(e) || e?.message || String(e)),
  });

  const stepTitles = [t("wizard.steps.cut"), t("wizard.steps.boundary"), t("wizard.steps.exclusions"), t("wizard.steps.confirm")];

  return (
    <Dialog open={open} onOpenChange={(next) => !close.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-3xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 4 ? t("wizard.done.title") : t("wizard.title", { name: season.name })}</DialogTitle>
          <DialogDescription>{step === 4 ? t("wizard.done.description") : t("wizard.description")}</DialogDescription>
        </DialogHeader>

        {step < 4 && (
          <ol className="grid grid-cols-4 gap-1.5">
            {stepTitles.map((title, idx) => (
              <li
                key={title}
                className={cn(
                  "rounded-md border px-2 py-1.5 text-xs leading-tight",
                  idx === step ? "border-primary bg-primary/5 font-semibold" : "text-muted-foreground",
                )}
              >
                <span className="font-mono mr-1">{idx + 1}</span>
                {title}
              </li>
            ))}
          </ol>
        )}

        {step === 4 ? (
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-3 text-sm">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <p>{t("wizard.done.body", { closed: closingName.trim(), active: closedSeason?.name || newName.trim() })}</p>
          </div>
        ) : !canClose ? (
          <p className="text-sm text-muted-foreground">{t("wizard.tooEarly")}</p>
        ) : isLoading || matchesQuery.isLoading ? (
          <SectionLoading />
        ) : (
          <>
            {step === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{t("wizard.cut.intro")}</p>
                <Timeline
                  sessions={sortedSessions}
                  seasonStart={season.starts_on}
                  today={today}
                  cutDate={cutDate}
                  maxCut={maxCut}
                  onPick={setCutDate}
                />
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="season-cut">{t("wizard.cut.label")}</Label>
                    <Input
                      id="season-cut"
                      type="date"
                      value={cutDate}
                      min={season.starts_on}
                      max={maxCut}
                      onChange={(e) => setCutDate(e.target.value)}
                      className="w-44"
                    />
                  </div>
                  {cutDate !== suggestedCut && (
                    <Button variant="ghost" size="sm" onClick={() => setCutDate(suggestedCut)}>
                      {t("wizard.cut.useSuggested", { date: formatDay(suggestedCut) })}
                    </Button>
                  )}
                </div>
                {cutError ? (
                  <p className="text-sm text-rose-600">{cutError}</p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Counter
                      tone="old"
                      title={t("wizard.cut.oldTitle", { name: season.name, from: formatDay(season.starts_on), to: formatDay(cutDate) })}
                      body={t("wizard.cut.oldBody", {
                        trainings: oldSessions.filter((s) => !s.has_matches).length,
                        matchDays: oldSessions.filter((s) => s.has_matches).length,
                        matches: oldMatches.length,
                      })}
                    />
                    <Counter
                      tone="new"
                      title={t("wizard.cut.newTitle", { from: formatDay(addDays(cutDate, 1)) })}
                      body={t("wizard.cut.newBody", { count: newSessions.length })}
                    />
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t("wizard.boundary.intro", { from: formatDay(zoneFrom), to: formatDay(zoneTo) })}
                </p>
                {zoneSessions.length === 0 ? (
                  <p className="rounded-xl bg-raised px-3 py-4 text-sm text-muted-foreground text-center">
                    {t("wizard.boundary.empty")}
                  </p>
                ) : (
                  <div className="rounded-lg border divide-y max-h-[45vh] overflow-y-auto">
                    {zoneSessions.map((session) => {
                      const side = sideOf(session);
                      const moved = side !== defaultSide(session);
                      const matchCount = matchesBySession.get(session.id) || 0;
                      return (
                        <div key={session.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
                          <span className="font-mono text-xs w-24 shrink-0">{formatDay(dateOf(session), true)}</span>
                          <div className="flex-1 min-w-[10rem]">
                            <p className="text-sm font-medium truncate">{session.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {session.has_matches
                                ? t("wizard.boundary.matchDay", { count: matchCount })
                                : t("wizard.boundary.training")}
                              {" · "}
                              {t("wizard.boundary.present", { count: presentBySession.get(session.id) || 0 })}
                              {moved && <span className="ml-1.5 text-amber-600 font-medium">{t("wizard.boundary.moved")}</span>}
                            </p>
                          </div>
                          <div className="inline-flex rounded-md border overflow-hidden text-xs font-mono">
                            {(["old", "new"] as Side[]).map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() =>
                                  setOverrides((prev) => {
                                    const next = new Map(prev);
                                    if (option === defaultSide(session)) next.delete(session.id);
                                    else next.set(session.id, option);
                                    return next;
                                  })
                                }
                                className={cn(
                                  "px-2.5 py-1 max-w-[9rem] truncate",
                                  side === option
                                    ? option === "old"
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-sky-600 text-white"
                                    : "text-muted-foreground hover:bg-muted/60",
                                )}
                              >
                                {option === "old" ? closingName || season.name : newName || t("wizard.newSeason")}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <ExclusionsStep
                matches={oldMatches}
                countsOf={countsOf}
                onToggle={(ids, value) =>
                  setMatchCounts((prev) => {
                    const next = new Map(prev);
                    for (const id of ids) next.set(id, value);
                    return next;
                  })
                }
                excludedCount={excludedCount}
              />
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="season-closing-name">{t("wizard.confirm.closingName")}</Label>
                    <Input id="season-closing-name" value={closingName} maxLength={60} onChange={(e) => setClosingName(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="season-new-name">{t("wizard.confirm.newName")}</Label>
                    <Input
                      id="season-new-name"
                      value={newName}
                      maxLength={60}
                      onChange={(e) => {
                        setNewName(e.target.value);
                        setNewNameDirty(true);
                      }}
                    />
                  </div>
                </div>
                {nameError && <p className="text-sm text-rose-600">{nameError}</p>}
                <div className="rounded-xl bg-raised p-3 text-sm space-y-1.5">
                  <p>
                    {t(excludedCount > 0 ? "wizard.confirm.summaryOld" : "wizard.confirm.summaryOldNoExcluded", {
                      name: closingName.trim() || season.name,
                      trainings: oldSessions.filter((s) => !s.has_matches).length,
                      matches: oldMatches.length,
                      excluded: excludedCount,
                    })}
                  </p>
                  <p>
                    {t("wizard.confirm.summaryNew", {
                      name: newName.trim() || t("wizard.newSeason"),
                      date: formatDay(addDays(cutDate, 1)),
                      count: newSessions.length,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("wizard.confirm.safety")}</p>
                </div>
                {needsExclusionAck && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm space-y-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <p>
                        {t(excludedCount === oldMatches.length ? "wizard.confirm.warnAll" : "wizard.confirm.warnMany", {
                          name: closingName.trim() || season.name,
                          excluded: excludedCount,
                          total: oldMatches.length,
                        })}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={exclusionAck} onChange={(e) => setExclusionAck(e.target.checked)} className="h-4 w-4" />
                      {t("wizard.confirm.ack")}
                    </label>
                  </div>
                )}
                {error && <p className="text-sm text-rose-600">{error}</p>}
              </div>
            )}

          </>
        )}

        <DialogFooter className="gap-2">
          {step === 4 ? (
            <Button onClick={() => onOpenChange(false)}>{t("wizard.done.close")}</Button>
          ) : (
            <>
              {step > 0 && (
                <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={close.isPending}>
                  {t("common.back")}
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={close.isPending}>
                {t("common.cancel")}
              </Button>
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canClose || !!cutError || isLoading}>
                  {step === 2 ? t("wizard.review") : t("common.next")}
                </Button>
              ) : (
                <Button onClick={() => close.mutate()} disabled={!!nameError || !!cutError || close.isPending || (needsExclusionAck && !exclusionAck)}>
                  {close.isPending ? t("wizard.confirm.closing") : t("wizard.confirm.submit")}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExclusionsStep({
  matches,
  countsOf,
  onToggle,
  excludedCount,
}: {
  matches: Match[];
  countsOf: (m: Match) => boolean;
  onToggle: (ids: string[], value: boolean) => void;
  excludedCount: number;
}) {
  const { t } = useTranslation("seasons");
  const locale = currentIntlLocale();
  const competitions = useMemo(() => {
    const map = new Map<string, Match[]>();
    for (const m of matches) {
      const key = m.competition_name?.trim() || "";
      if (!key) continue;
      map.set(key, [...(map.get(key) || []), m]);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [matches]);
  const byMonth = useMemo(() => {
    const map = new Map<string, Match[]>();
    for (const m of matches) {
      const key = romeDate(m.starts_at).slice(0, 7) || "-";
      map.set(key, [...(map.get(key) || []), m]);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [matches]);

  if (matches.length === 0) {
    return <p className="rounded-xl bg-raised px-3 py-4 text-sm text-muted-foreground text-center">{t("wizard.exclusions.empty")}</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("wizard.exclusions.intro")}</p>
      {competitions.length > 0 && (
        <div className="space-y-1.5">
          <p className="eb-eyebrow">{t("wizard.exclusions.byCompetition")}</p>
          <div className="rounded-lg border divide-y">
            {competitions.map(([name, list]) => {
              const counted = list.filter((m) => countsOf(m)).length;
              const allCounted = counted === list.length;
              return (
                <label key={name} className="flex items-center gap-3 px-3 py-1.5 text-sm cursor-pointer">
                  <span className={cn("flex-1 min-w-0 truncate", !allCounted && counted === 0 && "text-muted-foreground line-through")}>
                    {name}
                    <span className="text-muted-foreground"> · {t("wizard.exclusions.rowCount", { count: list.length })}</span>
                  </span>
                  <span className={cn("text-xs w-20 text-right", counted === 0 ? "text-rose-600" : "text-muted-foreground")}>
                    {counted === 0
                      ? t("wizard.exclusions.excluded")
                      : `${t("wizard.exclusions.counts")} ${counted}/${list.length}`}
                  </span>
                  <Switch
                    checked={allCounted}
                    onCheckedChange={(value) => onToggle(list.map((m) => m.id), value)}
                    aria-label={t("wizard.exclusions.toggleAria", { name })}
                  />
                </label>
              );
            })}
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{t("wizard.exclusions.count", { count: excludedCount, total: matches.length })}</p>
      <div className="rounded-lg border max-h-[45vh] overflow-y-auto">
        {byMonth.map(([month, list]) => (
          <div key={month}>
            <p className="sticky top-0 bg-muted/80 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground capitalize">
              {month === "-" ? month : new Date(`${month}-01T12:00:00`).toLocaleDateString(locale, { month: "long", year: "numeric" })}
            </p>
            {list.map((m) => {
              const counts = countsOf(m);
              return (
                <label key={m.id} className="flex items-center gap-3 px-3 py-1.5 border-t text-sm cursor-pointer">
                  <span className="font-mono text-xs w-14 shrink-0 text-muted-foreground">{formatDay(romeDate(m.starts_at))}</span>
                  <span className={cn("flex-1 min-w-0 truncate", !counts && "text-muted-foreground line-through")}>
                    {m.opponent_name}
                    {m.competition_name ? <span className="text-muted-foreground"> · {m.competition_name}</span> : null}
                  </span>
                  <span className="tabular-nums text-xs w-10 text-right">
                    {m.our_score != null && m.opponent_score != null ? `${m.our_score}-${m.opponent_score}` : "-"}
                  </span>
                  <Switch checked={counts} onCheckedChange={(value) => onToggle([m.id], value)} />
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function Counter({ tone, title, body }: { tone: "old" | "new"; title: string; body: string }) {
  return (
    <div className={cn("rounded-lg px-3 py-2 text-sm", tone === "old" ? "bg-primary/10" : "bg-sky-500/10")}>
      <p className="font-semibold leading-tight">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{body}</p>
    </div>
  );
}

/** Istogramma settimanale delle sessioni con la data di taglio. Clic su una settimana = taglio alla sua domenica. */
function Timeline({
  sessions,
  seasonStart,
  today,
  cutDate,
  maxCut,
  onPick,
}: {
  sessions: Session[];
  seasonStart: string;
  today: string;
  cutDate: string;
  maxCut: string;
  onPick: (date: string) => void;
}) {
  const { t } = useTranslation("seasons");
  const locale = currentIntlLocale();
  const weeks = useMemo(() => {
    const lastSession = sessions.length > 0 ? romeDate(sessions[sessions.length - 1].starts_at) : today;
    const end = addDays([today, lastSession].sort().pop() as string, 14);
    const first = mondayOf(seasonStart);
    const list: Array<{ start: string; end: string; trainings: number; matchDays: number }> = [];
    for (let d = first, guard = 0; d <= end && guard < 160; d = addDays(d, 7), guard += 1) {
      list.push({ start: d, end: addDays(d, 6), trainings: 0, matchDays: 0 });
    }
    for (const s of sessions) {
      const date = romeDate(s.starts_at);
      const idx = Math.floor(daysBetween(first, date) / 7);
      const week = list[idx];
      if (!week) continue;
      if (s.has_matches) week.matchDays += 1;
      else week.trainings += 1;
    }
    return list;
  }, [sessions, seasonStart, today]);

  if (weeks.length === 0) return null;
  const max = Math.max(3, ...weeks.map((w) => w.trainings + w.matchDays));
  const W = 700;
  const H = 150;
  const left = 8;
  const base = 112;
  const barW = (W - left * 2) / weeks.length;
  const unit = (base - 16) / max;
  const firstDay = weeks[0].start;
  const xOf = (date: string) => left + (daysBetween(firstDay, date) / 7) * barW;
  const cutX = cutDate ? xOf(addDays(cutDate, 1)) : null;
  const zoneX1 = cutDate ? xOf(addDays(cutDate, -BOUNDARY_DAYS)) : 0;
  const zoneX2 = cutDate ? xOf(addDays(cutDate, BOUNDARY_DAYS + 1)) : 0;
  let lastMonth = "";
  let lastLabelIdx = -10;

  return (
    <div className="rounded-xl bg-raised p-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pb-1 text-xs text-muted-foreground">
        <Legend className="bg-primary/40" label={t("wizard.timeline.trainings")} />
        <Legend className="bg-primary" label={t("wizard.timeline.matchDays")} />
        <Legend className="bg-sky-500" label={t("wizard.timeline.newSeason")} />
        <Legend className="bg-amber-200" label={t("wizard.timeline.boundary")} />
        <span className="ml-auto">{t("wizard.timeline.hint")}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={t("wizard.timeline.aria")}>
        {cutDate && (
          <rect x={zoneX1} y={4} width={Math.max(0, zoneX2 - zoneX1)} height={base - 4} className="fill-amber-100" />
        )}
        {weeks.map((week, idx) => {
          const x = left + idx * barW;
          const isNew = !!cutDate && week.end > cutDate;
          const isFuture = week.start > today;
          const pickable = week.end >= seasonStart && week.start <= maxCut;
          const md = week.matchDays * unit;
          const tr = week.trainings * unit;
          const month = week.start.slice(0, 7);
          const showMonth = month !== lastMonth && idx - lastLabelIdx >= 3;
          if (month !== lastMonth) lastMonth = month;
          if (showMonth) lastLabelIdx = idx;
          return (
            <g
              key={week.start}
              className={cn(pickable && "cursor-pointer")}
              onClick={() => pickable && onPick(week.end > maxCut ? maxCut : week.end < seasonStart ? seasonStart : week.end)}
            >
              <rect x={x} y={4} width={barW} height={base - 4} className="fill-transparent hover:fill-muted/60" />
              {md > 0 && (
                <rect
                  x={x + 1}
                  y={base - md}
                  width={Math.max(1, barW - 2)}
                  height={md}
                  rx={1}
                  className={cn(isNew ? "fill-sky-500" : "fill-primary", isFuture && "opacity-40")}
                />
              )}
              {tr > 0 && (
                <rect
                  x={x + 1}
                  y={base - md - tr}
                  width={Math.max(1, barW - 2)}
                  height={Math.max(0, tr - 1)}
                  rx={1}
                  className={cn(isNew ? "fill-sky-500/40" : "fill-primary/40", isFuture && "opacity-40")}
                />
              )}
              {showMonth && (
                <text x={x + 1} y={base + 14} className="fill-muted-foreground" fontSize="10">
                  {new Date(`${week.start}T12:00:00`).toLocaleDateString(locale, { month: "short" })}
                </text>
              )}
            </g>
          );
        })}
        <line x1={left} x2={W - left} y1={base} y2={base} className="stroke-border" strokeWidth={1} />
        {cutX != null && (
          <g pointerEvents="none">
            <line x1={cutX} x2={cutX} y1={2} y2={base + 20} className="stroke-amber-600" strokeWidth={2} />
            <circle cx={cutX} cy={base + 22} r={4} className="fill-amber-600" />
            <text
              x={cutX > W - 160 ? cutX - 8 : cutX + 8}
              y={base + 34}
              textAnchor={cutX > W - 160 ? "end" : "start"}
              className="fill-foreground"
              fontSize="11"
              fontWeight={600}
            >
              {t("wizard.timeline.cut", { date: formatDay(cutDate) })}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("inline-block h-2.5 w-2.5 rounded-sm", className)} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helper date
// ---------------------------------------------------------------------------

function daysBetween(from: string, to: string): number {
  return Math.round((new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime()) / 86_400_000);
}

function mondayOf(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  const offset = (d.getUTCDay() + 6) % 7;
  return addDays(isoDate, -offset);
}

function formatDay(isoDate: string, withWeekday = false): string {
  if (!isoDate) return "";
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString(currentIntlLocale(), {
    ...(withWeekday ? { weekday: "short" as const } : {}),
    day: "numeric",
    month: "short",
  });
}

/**
 * Taglio proposto: la pausa piu' lunga (almeno 14 giorni) tra sessioni negli
 * ultimi 6 mesi. Il taglio cade il giorno prima della ripresa; se non c'e'
 * ancora ripresa, ieri. Senza una pausa evidente, ieri.
 */
function suggestCutDate(sessions: Session[], seasonStart: string, today: string): string {
  const yesterday = addDays(today, -1);
  const clamp = (d: string) => (d < seasonStart ? seasonStart : d > yesterday ? yesterday : d);
  const dates = [...new Set(sessions.map((s) => romeDate(s.starts_at)).filter(Boolean))].sort();
  const windowStart = addDays(today, -180);
  let best: { gap: number; cut: string } | null = null;
  dates.forEach((date, idx) => {
    if (date > today || date < windowStart) return;
    const next = dates[idx + 1];
    const until = next ?? today;
    const gap = daysBetween(date, until);
    const cut = next ? addDays(next, -1) : yesterday;
    if (!best || gap > best.gap) best = { gap, cut };
  });
  const found = best as { gap: number; cut: string } | null;
  if (found && found.gap >= 14) return clamp(found.cut);
  return clamp(yesterday);
}
